import api from './api';
import { LoginCredentials, AuthResponse, ApiError } from '../types';
import orgService from './orgService';
import fcmService from './fcmService';

class AuthService {
  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/auth/signin', credentials);
      const { access_token, refresh_token, token_type } = response.data;
      
      // Store tokens in localStorage
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('token_type', token_type);
      
      return response.data;
    } catch (error) {
      throw this.handleError(error as ApiError);
    }
  }

  // Logout user
  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_type');
    // Clear organization_id on logout
    orgService.clearOrganizationId();
    // Clear role on logout
    orgService.clearRole();
    // Clean up FCM service
    fcmService.cleanup();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  // Get stored access token
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  // Handle API errors
  handleError(error: ApiError): Error {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          return new Error('Invalid credentials. Please check your username and password.');
        case 403:
          return new Error('Access denied. You do not have permission to access this resource.');
        case 404:
          return new Error('User not found. Please check your username and password.');
        case 500:
          return new Error('Internal server error. Please try again later.');
        default:
          return new Error(data?.message || 'An unexpected error occurred. Please try again.');
      }
    } else if (error.request) {
      // Network error
      return new Error('Network error. Please check your internet connection and try again.');
    } else {
      // Other error
      return new Error('An unexpected error occurred. Please try again.');
    }
  }
}

const authService = new AuthService();
export default authService;
